import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FaGripLinesVertical } from "react-icons/fa";
import { useResponsive } from "../contexts/ResponsiveContext";

/**
 * ResizablePanels - A reusable component that renders two panels with a draggable divider
 *
 * @param {object} props
 * @param {React.ReactNode} props.leftPanel - Content for the left panel
 * @param {React.ReactNode} props.rightPanel - Content for the right panel
 * @param {number} props.initialSplit - Initial split percentage (20-80)
 * @param {number} props.minLeftWidth - Minimum width percentage for left panel
 * @param {number} props.maxLeftWidth - Maximum width percentage for left panel
 * @param {object} props.theme - Theme object for styling
 * @param {string} props.className - Additional class name for the container
 * @param {string} props.dividerColor - Color for the divider line
 * @param {string} props.dividerActiveColor - Color for the divider when active
 * @param {string} props.direction - Split direction: 'horizontal' or 'vertical' (default: 'vertical')
 * @returns {React.ReactElement}
 */
const ResizablePanels = ({
  leftPanel,
  rightPanel,
  initialSplit = 50,
  minLeftWidth = 20,
  maxLeftWidth = 80,
  theme = {},
  className = "",
  dividerColor,
  dividerActiveColor,
  direction = "vertical",
}) => {
  // Get responsive context for mobile detection
  const { isMobile } = useResponsive();

  // Initialize panel width state with the initial split value
  const [leftPanelWidth, setLeftPanelWidth] = useState(initialSplit);
  const [isDragging, setIsDragging] = useState(false);

  // Ref for the container element
  const containerRef = useRef(null);

  // Use theme values or defaults
  const primaryColor = dividerColor || theme.primaryColor || "#3f51b5";
  const accentColor = dividerActiveColor || theme.accentColor || "#f50057";
  const backgroundColor = theme.backgroundColor || "#ffffff";
  const textColor = theme.textColor || "#333333";

  // Determine if we're using vertical or horizontal split
  const isVertical = direction === "vertical";

  // Handle mouse down event on the divider
  const handleMouseDown = (e) => {
    if (isMobile) return;

    // Prevent default behavior to avoid text selection
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);

    // Define the move and up handlers inside to have access to closure variables
    const handleMove = (moveEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();

      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerSize = isVertical
        ? containerRect.width
        : containerRect.height;
      const mousePosition = isVertical ? moveEvent.clientX : moveEvent.clientY;
      const containerStart = isVertical
        ? containerRect.left
        : containerRect.top;

      // Calculate percentage with constraints
      const positionPercentage =
        ((mousePosition - containerStart) / containerSize) * 100;
      const newWidth = Math.max(
        minLeftWidth,
        Math.min(maxLeftWidth, positionPercentage)
      );

      setLeftPanelWidth(newWidth);
    };

    const handleUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    // Apply global styles
    document.body.style.cursor = isVertical ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";

    // Add event listeners
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  };

  // Handle touch events for mobile/touch devices
  const handleTouchStart = (e) => {
    if (isMobile || !e.touches || !e.touches[0]) return;

    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);

    const handleTouchMove = (moveEvent) => {
      if (!containerRef.current || !moveEvent.touches || !moveEvent.touches[0])
        return;

      moveEvent.preventDefault();
      moveEvent.stopPropagation();

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerSize = isVertical
        ? containerRect.width
        : containerRect.height;
      const touchPosition = isVertical
        ? moveEvent.touches[0].clientX
        : moveEvent.touches[0].clientY;
      const containerStart = isVertical
        ? containerRect.left
        : containerRect.top;

      const positionPercentage =
        ((touchPosition - containerStart) / containerSize) * 100;
      const newWidth = Math.max(
        minLeftWidth,
        Math.min(maxLeftWidth, positionPercentage)
      );

      setLeftPanelWidth(newWidth);
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.removeEventListener("touchend", handleTouchEnd);
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  // Define styles for divider component
  const dividerStyles = {
    container: {
      position: "absolute",
      [isVertical ? "left" : "top"]: `${leftPanelWidth}%`,
      [isVertical ? "top" : "left"]: 0,
      [isVertical ? "bottom" : "right"]: 0,
      width: isVertical ? "30px" : undefined,
      height: isVertical ? undefined : "30px",
      marginLeft: isVertical ? "-15px" : undefined,
      marginTop: isVertical ? undefined : "-15px",
      cursor: isVertical ? "col-resize" : "row-resize",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      touchAction: "none",
      backgroundColor: isDragging ? "rgba(0,0,0,0.05)" : "transparent",
    },

    line: {
      position: "absolute",
      [isVertical ? "top" : "left"]: 0,
      [isVertical ? "bottom" : "right"]: 0,
      [isVertical ? "left" : "top"]: "50%",
      width: isVertical ? "4px" : undefined,
      height: isVertical ? undefined : "4px",
      backgroundColor: isDragging ? accentColor : primaryColor,
      opacity: isDragging ? 1 : 0.7,
      transform: isVertical ? "translateX(-50%)" : "translateY(-50%)",
      boxShadow: "0 0 5px rgba(0,0,0,0.2)",
    },

    handle: {
      width: isVertical ? "28px" : "50px",
      height: isVertical ? "50px" : "28px",
      borderRadius: "4px",
      backgroundColor: backgroundColor,
      border: `2px solid ${isDragging ? accentColor : primaryColor}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      position: "relative",
      boxShadow: isDragging
        ? "0 0 10px rgba(0,0,0,0.3)"
        : "0 1px 3px rgba(0,0,0,0.1)",
      transition: "transform 0.15s, box-shadow 0.15s",
      transform: isDragging ? "scale(1.1)" : "scale(1)",
    },

    tooltip: {
      position: "absolute",
      [isVertical ? "top" : "left"]: "-25px",
      [isVertical ? "left" : "top"]: "50%",
      transform: isVertical ? "translateX(-50%)" : "translateY(-50%)",
      backgroundColor: isDragging ? accentColor : primaryColor,
      color: "#fff",
      padding: "2px 6px",
      borderRadius: "3px",
      fontSize: "12px",
      opacity: isDragging ? 1 : 0,
      transition: "opacity 0.2s",
      whiteSpace: "nowrap",
      pointerEvents: "none",
    },
  };

  // Render the divider - only if not in mobile mode
  const renderDivider = () => {
    if (isMobile) return null;

    return (
      <>
        <div
          style={dividerStyles.container}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
          <div style={dividerStyles.line} />
          <div style={dividerStyles.handle}>
            <FaGripLinesVertical
              size={18}
              color={isDragging ? accentColor : primaryColor}
              style={{
                transform: isVertical ? "none" : "rotate(90deg)",
                opacity: 0.9,
              }}
            />
            <div style={dividerStyles.tooltip}>
              {Math.round(leftPanelWidth)}%
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`resizable-panels-container ${
        isVertical ? "flex-row" : "flex-col"
      } ${className}`}
      style={{
        display: isMobile ? "block" : "flex",
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: isMobile ? "auto" : "hidden",
        flexDirection: isVertical ? "row" : "column",
        backgroundColor: backgroundColor,
      }}
    >
      {/* Left/Top Panel */}
      <div
        className={`resizable-panel resizable-panel-left ${
          isMobile ? "resizable-panel-mobile" : ""
        }`}
        style={{
          width: isVertical
            ? `${isMobile ? "100%" : `${leftPanelWidth}%`}`
            : "100%",
          height: isVertical
            ? "100%"
            : `${isMobile ? "auto" : `${leftPanelWidth}%`}`,
          minHeight: isMobile ? "auto" : "auto",
          overflow: isMobile ? "visible" : "auto",
          position: "relative",
          backgroundColor: backgroundColor, // Use the theme background color
          color: textColor, // Use the theme text color
          boxSizing: "border-box",
          paddingRight: isVertical && !isMobile ? "10px" : "0",
          paddingBottom: !isVertical && !isMobile ? "10px" : "0",
        }}
      >
        {leftPanel}
      </div>

      {/* Divider */}
      {renderDivider()}

      {/* Right/Bottom Panel */}
      <div
        className={`resizable-panel resizable-panel-right ${
          isMobile ? "resizable-panel-mobile" : ""
        }`}
        style={{
          width: isVertical
            ? `${isMobile ? "100%" : `${100 - leftPanelWidth}%`}`
            : "100%",
          height: isVertical
            ? "100%"
            : `${isMobile ? "auto" : `${100 - leftPanelWidth}%`}`,
          minHeight: isMobile ? "auto" : "auto",
          overflow: isMobile ? "visible" : "auto",
          position: "relative",
          display: "block",
          backgroundColor: backgroundColor, // Use the theme background color
          color: textColor, // Use the theme text color
          boxSizing: "border-box",
          paddingLeft: isVertical && !isMobile ? "10px" : "0",
          paddingTop: !isVertical && !isMobile ? "10px" : "0",
          marginTop: isMobile ? "20px" : "0",
        }}
      >
        {rightPanel}
      </div>
    </div>
  );
};

ResizablePanels.propTypes = {
  leftPanel: PropTypes.node.isRequired,
  rightPanel: PropTypes.node.isRequired,
  initialSplit: PropTypes.number,
  minLeftWidth: PropTypes.number,
  maxLeftWidth: PropTypes.number,
  theme: PropTypes.object,
  className: PropTypes.string,
  dividerColor: PropTypes.string,
  dividerActiveColor: PropTypes.string,
  direction: PropTypes.oneOf(["vertical", "horizontal"]),
};

export default ResizablePanels;
